import React, { memo } from 'react';
import { View, Text, TouchableOpacity, Switch, StyleSheet } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { GlassCard } from '@/components/shared/redesign';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { TagPill } from './TagPill';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagListItemProps {
  tag: CorkBoardTag;
  isFirst?: boolean;
  isLast?: boolean;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
  onMoveUp?: (tagId: number) => void;
  onMoveDown?: (tagId: number) => void;
}

const arePropsEqual = (prev: TagListItemProps, next: TagListItemProps): boolean =>
  prev.tag.id === next.tag.id &&
  prev.tag.name === next.tag.name &&
  prev.tag.color === next.tag.color &&
  prev.tag.icon === next.tag.icon &&
  prev.tag.isEnabled === next.tag.isEnabled &&
  prev.isFirst === next.isFirst &&
  prev.isLast === next.isLast;

export const TagListItem: React.FC<TagListItemProps> = memo(
  ({ tag, isFirst, isLast, onToggleEnabled, onEdit, onDelete, onMoveUp, onMoveDown }) => {
    const { redesign, semantic } = useAdminColors();
    const { components } = useThemeColors();
    const disabled = !tag.isEnabled;

    return (
      <GlassCard padding={0} style={styles.cardRoot}>
        <View style={styles.container}>
          <View style={styles.topRow}>
            <TagPill name={tag.name} color={tag.color} icon={tag.icon} disabled={disabled} />
            {disabled ? (
              <Text
                style={[
                  styles.hiddenBadge,
                  { color: redesign.text.subtle, backgroundColor: redesign.glass.buttonBg },
                ]}
              >
                HIDDEN
              </Text>
            ) : null}
            <View style={styles.spacer} />
            <Switch
              value={tag.isEnabled}
              onValueChange={() => onToggleEnabled(tag)}
              trackColor={{ false: redesign.separator, true: components.toggle.trackOn }}
              thumbColor="#FFFFFF"
              ios_backgroundColor={redesign.separator}
              accessibilityLabel={`${tag.isEnabled ? 'Disable' : 'Enable'} ${tag.name}`}
            />
          </View>

          <View style={[styles.divider, { backgroundColor: redesign.card.border }]} />

          <View style={styles.bottomRow}>
            <View style={styles.reorderGroup}>
              <TouchableOpacity
                style={[
                  styles.iconButton,
                  {
                    backgroundColor: redesign.glass.buttonBg,
                    borderColor: redesign.card.border,
                  },
                  isFirst && styles.iconButtonDisabled,
                ]}
                onPress={() => onMoveUp?.(tag.id)}
                disabled={isFirst}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                accessibilityLabel={`Move ${tag.name} up`}
                accessibilityRole="button"
              >
                <SVG_COMPONENTS.CHEVRON_UP
                  width={12}
                  height={12}
                  color={isFirst ? redesign.text.subtle : redesign.text.primary}
                />
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.iconButton,
                  {
                    backgroundColor: redesign.glass.buttonBg,
                    borderColor: redesign.card.border,
                  },
                  isLast && styles.iconButtonDisabled,
                ]}
                onPress={() => onMoveDown?.(tag.id)}
                disabled={isLast}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                accessibilityLabel={`Move ${tag.name} down`}
                accessibilityRole="button"
              >
                <SVG_COMPONENTS.CHEVRON_DOWN
                  width={12}
                  height={12}
                  color={isLast ? redesign.text.subtle : redesign.text.primary}
                />
              </TouchableOpacity>
            </View>

            <View style={styles.actionsGroup}>
              <TouchableOpacity
                style={[
                  styles.iconButton,
                  {
                    backgroundColor: redesign.glass.buttonBg,
                    borderColor: redesign.card.border,
                  },
                ]}
                onPress={() => onEdit(tag)}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                accessibilityLabel={`Edit ${tag.name}`}
                accessibilityRole="button"
              >
                <SVG_COMPONENTS.PENCIL width={14} height={14} color={PRIMITIVE_COLORS.resioBlue} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.iconButton,
                  {
                    backgroundColor: redesign.glass.buttonBg,
                    borderColor: redesign.card.border,
                  },
                ]}
                onPress={() => onDelete(tag)}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                accessibilityLabel={`Delete ${tag.name}`}
                accessibilityRole="button"
              >
                <SVG_COMPONENTS.TRASH width={14} height={14} color={semantic.status.error} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </GlassCard>
    );
  },
  arePropsEqual,
);

TagListItem.displayName = 'TagListItem';

const styles = StyleSheet.create({
  cardRoot: {
    marginBottom: 8,
  },
  container: {
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  spacer: {
    flex: 1,
  },
  hiddenBadge: {
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 0.5,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  divider: {
    height: 1,
    marginVertical: 10,
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  reorderGroup: {
    flexDirection: 'row',
    gap: 6,
  },
  actionsGroup: {
    flexDirection: 'row',
    gap: 6,
  },
  iconButton: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  iconButtonDisabled: {
    opacity: 0.3,
  },
});
